// ToursPage: list with filters and sort (front-end filtering)
import React, { useEffect, useState } from "react";
import FilterBar from "../components/FilterBar";
import TourCard from "../components/TourCard";
import Loader from "../components/Loader";
import SEO from "../components/SEO";
import tourApi from "../api/tour";
import AnimatedPage from "../components/AnimatedPage";
import SlideInWhenVisible from "../components/SlideInWhenVisible";

export default function ToursPage() {
  const [filters, setFilters] = useState({});
  const [tours, setTours] = useState([]); // tous les tours récupérés
  const [filteredTours, setFilteredTours] = useState([]); // tours filtrés
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const toursPerPage = 6;

  // Chargement initial des tours
  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await tourApi.listTours(); // appel unique
        if (active) {
          // ✅ Filtrer les tours passés (cacher les tours dont la start_date est dans le passé)
          const today = new Date().toISOString().split('T')[0]; // Format YYYY-MM-DD
          const availableTours = data.filter(tour => {
            // Si pas de start_date, afficher le tour
            if (!tour.start_date) return true;
            // Sinon, vérifier que start_date >= aujourd'hui
            const tourDate = tour.start_date.split('T')[0];
            return tourDate >= today;
          });
          setTours(availableTours);
          setFilteredTours(availableTours); // afficher tout au départ
        }
      } catch (err) {
        console.error("Erreur lors du chargement des tours :", err);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let filtered = [...tours];

    if (filters.q) {
      const q = filters.q.toLowerCase();
      filtered = filtered.filter(
        (t) =>
          t.title?.toLowerCase().includes(q) ||
          t.description?.toLowerCase().includes(q) ||
          t.city?.toLowerCase().includes(q)
      );
    }

    if (filters.city) {
      const city = filters.city.toLowerCase();
      filtered = filtered.filter((t) => t.city?.toLowerCase().includes(city));
    }

    if (filters.minPrice) {
      filtered = filtered.filter((t) => t.price >= Number(filters.minPrice));
    }

    if (filters.maxPrice) {
      filtered = filtered.filter((t) => t.price <= Number(filters.maxPrice));
    }

    if (filters.sortBy) {
      if (filters.sortBy === "price_asc") {
        filtered.sort((a, b) => a.price - b.price);
      } else if (filters.sortBy === "price_desc") {
        filtered.sort((a, b) => b.price - a.price);
      } else if (filters.sortBy === "rating_desc") {
        filtered.sort((a, b) => (b.rating || 0) - (a.rating || 0));
      }
    }

    setFilteredTours(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  }, [filters, tours]);

  // Calculate pagination
  const indexOfLastTour = currentPage * toursPerPage;
  const indexOfFirstTour = indexOfLastTour - toursPerPage;
  const currentTours = filteredTours.slice(indexOfFirstTour, indexOfLastTour);
  const totalPages = Math.ceil(filteredTours.length / toursPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <AnimatedPage>
      <SEO
        title="Morocco Tours - Sahara Desert Trips & Excursions"
        description="Browse all Morocco tours: Sahara desert trips, camel trekking, Atlas Mountains hikes, Marrakech day trips, and more. Find your perfect Moroccan adventure."
        path="/tours"
      />
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">All Tours</h1>
          {filteredTours.length > 0 && (
            <span className="text-sm text-slate-600 dark:text-slate-300">
              Showing {indexOfFirstTour + 1}-
              {Math.min(indexOfLastTour, filteredTours.length)} of{" "}
              {filteredTours.length} tours
            </span>
          )}
        </div>

        <FilterBar filters={filters} onChange={setFilters} />

        {loading ? (
          <Loader />
        ) : filteredTours.length === 0 ? (
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-12 text-center dark:border-slate-800 dark:bg-slate-900">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-slate-200 dark:bg-slate-800">
              <span className="text-3xl">🔍</span>
            </div>
            <h3 className="mb-2 text-lg font-semibold text-slate-900 dark:text-slate-100">
              No tours found
            </h3>
            <p className="text-sm text-slate-600 dark:text-slate-300">
              Try adjusting your filters to find more tours
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              {currentTours.map((t) => (
                <SlideInWhenVisible
                  key={t.id}
                  direction="up"
                  delay={0.5}
                  duration={0.5}
                >
                  <TourCard tour={t} />
                </SlideInWhenVisible>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 pt-8">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
                >
                  Previous
                </button>

                <div className="flex gap-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                    (pageNumber) => {
                      // Show first page, last page, current page, and pages around current
                      if (
                        pageNumber === 1 ||
                        pageNumber === totalPages ||
                        (pageNumber >= currentPage - 1 &&
                          pageNumber <= currentPage + 1)
                      ) {
                        return (
                          <button
                            key={pageNumber}
                            onClick={() => handlePageChange(pageNumber)}
                            className={`min-w-[40px] rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                              currentPage === pageNumber
                                ? "bg-emerald-600 text-white"
                                : "border border-slate-300 text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
                            }`}
                          >
                            {pageNumber}
                          </button>
                        );
                      } else if (
                        pageNumber === currentPage - 2 ||
                        pageNumber === currentPage + 2
                      ) {
                        return (
                          <span
                            key={pageNumber}
                            className="px-2 py-2 text-slate-500"
                          >
                            ...
                          </span>
                        );
                      }
                      return null;
                    }
                  )}
                </div>

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </AnimatedPage>
  );
}
